<template>
  <div class="appointments-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="appointments-container">
          <!-- Filters Section -->
          <div class="filters-section">
            <div class="filters-row">
              <div class="filter-group">
                <label class="filter-label">Date</label>
                <input
                  v-model="filters.date"
                  type="date"
                  class="filter-input"
                  @change="loadAppointments"
                />
              </div>

              <div class="filter-group">
                <label class="filter-label">Status</label>
                <select v-model="filters.status" class="filter-select" @change="loadAppointments">
                  <option value="">All Statuses</option>
                  <option value="booked">Booked</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div class="filter-group">
                <label class="filter-label">Items per page</label>
                <select v-model="filters.limit" class="filter-select" @change="loadAppointments">
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div class="action-buttons">
                <button @click="clearFilters" class="btn-secondary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                  </svg>
                  Clear Filters
                </button>

                <button @click="showSettingsModal = true" class="btn-settings">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                  </svg>
                  Appointment System Settings
                </button>

                <button @click="showWeeklyScheduleModal = true" class="btn-schedule">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5M7,10H9V12H7V10M15,10H17V12H15V10M11,10H13V12H11V10M7,14H9V16H7V14M15,14H17V16H15V14M11,14H13V16H11V14Z"/>
                  </svg>
                  Weekly Schedule Settings
                </button>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading appointments...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="error-icon">
              <path d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4H5V21H19V9Z"/>
            </svg>
            <h3>Error Loading Appointments</h3>
            <p>{{ error }}</p>
            <button @click="loadAppointments" class="btn-primary">Try Again</button>
          </div>

          <!-- Appointments Table -->
          <div v-else class="table-container">
            <div v-if="appointments.length === 0" class="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
                <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5Z"/>
              </svg>
              <h3>No Appointments Found</h3>
              <p>{{ hasActiveFilters ? 'No appointments match your current filters.' : 'No appointments have been scheduled yet.' }}</p>
            </div>

            <div v-else class="table-wrapper">
              <table class="appointments-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Date</th>
                    <th>Time Slot</th>
                    <th>Status</th>
                    <th>Feedback</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="appointment in appointments" :key="appointment.id" class="appointment-row">
                    <td>
                      <div class="customer-info">
                        <span class="customer-name">{{ appointment.customer_name }}</span>
                      </div>
                    </td>
                    <td>
                      <a :href="`tel:${appointment.customer_phone}`" class="phone-link">
                        {{ formatPhone(appointment.customer_phone) }}
                      </a>
                    </td>
                    <td>
                      <span class="appointment-date">{{ formatDate(appointment.appointment_date) }}</span>
                    </td>
                    <td>
                      <span class="time-slot">{{ appointment.time_slot }}</span>
                    </td>
                    <td>
                      <span :class="getStatusClass(appointment.status)" class="status-badge">
                        {{ getStatusText(appointment.status) }}
                      </span>
                    </td>
                    <td>
                      <div v-if="appointment.status === 'completed' && (appointment.rating_stars || appointment.feedback)" class="feedback-cell">
                        <div v-if="appointment.rating_stars" class="rating" v-html="appointment.rating_stars"></div>
                        <div v-if="appointment.feedback" class="feedback-text" :title="appointment.feedback">
                          {{ truncateFeedback(appointment.feedback) }}
                        </div>
                      </div>
                      <span v-else class="no-feedback">-</span>
                    </td>
                    <td>
                      <span class="created-date">{{ formatDateTime(appointment.created_at) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="pagination && pagination.total > 0" class="pagination">
              <div class="pagination-info">
                Showing {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} to
                {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
                of {{ pagination.total }} appointments
              </div>

              <div class="pagination-controls">
                <button
                  @click="goToPage(pagination.current_page - 1)"
                  :disabled="pagination.current_page <= 1"
                  class="pagination-btn"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"/>
                  </svg>
                  Previous
                </button>

                <div class="page-numbers">
                  <button
                    v-for="page in getPageNumbers()"
                    :key="page"
                    @click="goToPage(page)"
                    :class="{ active: page === pagination.current_page }"
                    class="page-btn"
                  >
                    {{ page }}
                  </button>
                </div>

                <button
                  @click="goToPage(pagination.current_page + 1)"
                  :disabled="pagination.current_page >= pagination.last_page"
                  class="pagination-btn"
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Settings Modal -->
    <div v-if="showSettingsModal" class="modal-overlay" @click="closeSettingsModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2 class="modal-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="modal-icon">
              <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
            </svg>
            Appointment System Settings
          </h2>
          <button @click="closeSettingsModal" class="modal-close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="settings-section">
            <!-- Calendar Links Section -->
            <div class="setting-item">
              <div class="setting-info">
                <h3 class="setting-title">Send Calendar Link</h3>
                <p class="setting-description">{{ calendarLinkDescription }}</p>
              </div>
              <div class="setting-control">
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    v-model="calendarLinksEnabled"
                    @change="toggleCalendarLinks"
                    :disabled="settingsLoading"
                  />
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>

            <!-- Down Payment Settings Section -->
            <div class="payment-settings-section">
              <h3 class="section-title">Down Payment Settings</h3>

              <div class="setting-item payment-required-toggle">
                <div class="setting-info">
                  <h4 class="setting-title">Require Payment</h4>
                  <p class="setting-description">{{ paymentRequiredDescription }}</p>
                </div>
                <div class="setting-control">
                  <label class="toggle-switch">
                    <input
                      type="checkbox"
                      v-model="paymentSettings.payment_required"
                      @change="onPaymentRequiredChange"
                      :disabled="paymentLoading"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div v-if="paymentSettings.payment_required" class="payment-form">
                <div class="form-group">
                  <label class="form-label">Payment Amount</label>
                  <div class="input-with-currency">
                    <input
                      type="number"
                      v-model.number="paymentSettings.payment_amount"
                      class="form-input"
                      placeholder="50"
                      min="1"
                      step="1"
                      :disabled="paymentLoading"
                    />
                    <span class="currency-label">EGP</span>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">InstaPay Account</label>
                  <input
                    type="text"
                    v-model="paymentSettings.instapay_account"
                    class="form-input"
                    placeholder="5078035062298270"
                    :disabled="paymentLoading"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">InstaPay Link</label>
                  <input
                    type="url"
                    v-model="paymentSettings.instapay_link"
                    class="form-input"
                    placeholder="https://ipn.eg/S/username/instapay/code"
                    :disabled="paymentLoading"
                  />
                </div>

                <div class="form-actions">
                  <button
                    @click="savePaymentSettings"
                    :disabled="paymentLoading || !isPaymentFormValid"
                    class="btn-save"
                  >
                    <svg v-if="paymentLoading" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="loading-icon">
                      <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17,9H7V7L3,11L7,15V13H17V15L21,11L17,7V9Z"/>
                    </svg>
                    {{ paymentLoading ? 'Saving...' : 'Save Payment Settings' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeSettingsModal" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Weekly Schedule Modal -->
    <div v-if="showWeeklyScheduleModal" class="modal-overlay" @click="closeWeeklyScheduleModal">
      <div class="modal-content weekly-schedule-modal" @click.stop>
        <div class="modal-header">
          <h2 class="modal-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="modal-icon schedule-icon">
              <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5M7,10H9V12H7V10M15,10H17V12H15V10M11,10H13V12H11V10M7,14H9V16H7V14M15,14H17V16H15V14M11,14H13V16H11V14Z"/>
            </svg>
            Weekly Schedule Settings
          </h2>
          <button @click="closeWeeklyScheduleModal" class="modal-close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="scheduleLoading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading schedule...</p>
          </div>

          <!-- Schedule Content -->
          <div v-else class="schedule-content">
            <div v-if="weeklySchedule.length === 0" class="no-schedule-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="no-schedule-icon">
                <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5M7,10H9V12H7V10M15,10H17V12H15V10M11,10H13V12H11V10M7,14H9V16H7V14M15,14H17V16H15V14M11,14H13V16H11V14Z"/>
              </svg>
              <h3>No Weekly Schedule Set</h3>
              <p>Set your working hours for each day of the week to start accepting appointments.</p>
              <button @click="createNewSchedule" class="btn-primary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                </svg>
                Set Weekly Schedule
              </button>
            </div>

            <!-- Existing Schedule Display -->
            <div v-else class="existing-schedule">
              <div class="schedule-header">
                <h3>Current Weekly Schedule</h3>
                <button @click="editSchedule" class="btn-secondary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                  </svg>
                  Edit Schedule
                </button>
              </div>

              <div class="schedule-grid">
                <div
                  v-for="day in weeklySchedule"
                  :key="day.day_of_week"
                  :class="['schedule-day', { 'inactive': !day.is_active }]"
                >
                  <div class="day-header">
                    <h4>{{ day.day_name }}</h4>
                    <span :class="['status-indicator', day.is_active ? 'active' : 'inactive']">
                      {{ day.is_active ? 'Open' : 'Closed' }}
                    </span>
                  </div>

                  <div v-if="day.is_active" class="day-details">
                    <p class="working-hours">{{ day.working_hours }}</p>
                    <p class="slot-info">{{ day.slot_duration }}min slots</p>
                  </div>

                  <!-- Individual Day Actions -->
                  <div class="day-actions">
                    <button
                      v-if="day.is_active"
                      @click="editSingleDay(day)"
                      class="day-btn day-btn-edit"
                      :disabled="scheduleLoading"
                      title="Edit this day"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                      </svg>
                    </button>

                    <button
                      v-if="day.is_active"
                      @click="disableSingleDay(day)"
                      class="day-btn day-btn-disable"
                      :disabled="scheduleLoading"
                      title="Close this day"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                      </svg>
                    </button>

                    <button
                      v-else
                      @click="enableSingleDay(day)"
                      class="day-btn day-btn-enable"
                      :disabled="scheduleLoading"
                      title="Open this day"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule Form (when editing/creating) -->
            <div v-if="showScheduleForm" class="schedule-form">
              <div class="form-header">
                <h3>{{ weeklySchedule.length > 0 ? 'Update' : 'Set' }} Weekly Schedule</h3>
                <button @click="showScheduleForm = false" class="btn-text">Cancel</button>
              </div>

              <div class="bulk-schedule-form">
                <!-- Mixed Schedule Warning -->
                <div v-if="hasMixedScheduleTimes" class="mixed-schedule-warning">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="warning-icon">
                    <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z"/>
                  </svg>
                  <div class="warning-content">
                    <p><strong>Mixed Schedule Detected</strong></p>
                    <p>Your current schedule has different times for different days. The form shows the most common times. Saving will apply the same schedule to all selected days.</p>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">Select Days</label>
                  <div class="days-selector">
                    <label v-for="day in dayOptions" :key="day.value" class="day-checkbox">
                      <input
                        type="checkbox"
                        :value="day.value"
                        v-model="scheduleForm.days"
                      />
                      <span class="day-label">{{ day.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="time-inputs-row">
                  <div class="form-group">
                    <label class="form-label">Start Time</label>
                    <input
                      type="time"
                      v-model="scheduleForm.start_time"
                      class="form-input time-input"
                      :disabled="scheduleForm.days.length === 0"
                    />
                  </div>

                  <div class="form-group">
                    <label class="form-label">End Time</label>
                    <input
                      type="time"
                      v-model="scheduleForm.end_time"
                      class="form-input time-input"
                      :disabled="scheduleForm.days.length === 0"
                    />
                  </div>

                  <div class="form-group">
                    <label class="form-label">Slot Duration (minutes)</label>
                    <select
                      v-model="scheduleForm.slot_duration"
                      class="form-input"
                      :disabled="scheduleForm.days.length === 0"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 hour</option>
                    </select>
                  </div>
                </div>

                <div class="form-actions">
                  <button
                    @click="saveSchedule"
                    :disabled="!isScheduleFormValid || scheduleLoading"
                    class="btn-save"
                  >
                    <svg v-if="scheduleLoading" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="loading-icon">
                      <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4H5V21H19V9Z"/>
                    </svg>
                    {{ scheduleLoading ? 'Saving...' : (weeklySchedule.length > 0 ? 'Update Schedule' : 'Save Schedule') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeWeeklyScheduleModal" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Single Day Edit Modal -->
    <div v-if="showSingleDayModal" class="modal-overlay" @click="closeSingleDayModal">
      <div class="modal-content single-day-modal" @click.stop>
        <div class="modal-header">
          <h2 class="modal-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="modal-icon schedule-icon">
              <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5M7,10H9V12H7V10M15,10H17V12H15V10M11,10H13V12H11V10M7,14H9V16H7V14M15,14H17V16H15V14M11,14H13V16H11V14Z"/>
            </svg>
            Edit {{ singleDayForm.day_name }} Schedule
          </h2>
          <button @click="closeSingleDayModal" class="modal-close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="single-day-form">
            <div class="current-schedule-info">
              <h3>Current Schedule</h3>
              <div class="current-info-grid">
                <div class="info-item">
                  <label>Working Hours</label>
                  <span>{{ singleDayForm.working_hours || 'Not set' }}</span>
                </div>
                <div class="info-item">
                  <label>Slot Duration</label>
                  <span>{{ singleDayForm.slot_duration }}min</span>
                </div>
                <div class="info-item">
                  <label>Status</label>
                  <span :class="['status-badge', singleDayForm.is_active ? 'status-active' : 'status-inactive']">
                    {{ singleDayForm.is_active ? 'Open' : 'Closed' }}
                  </span>
                </div>
              </div>
            </div>

            <div class="single-day-edit-form">
              <h3>Update Schedule</h3>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Start Time</label>
                  <input
                    type="time"
                    v-model="singleDayForm.start_time"
                    class="form-input time-input"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">End Time</label>
                  <input
                    type="time"
                    v-model="singleDayForm.end_time"
                    class="form-input time-input"
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Slot Duration</label>
                  <select v-model="singleDayForm.slot_duration" class="form-input">
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 hour</option>
                  </select>
                </div>
              </div>

              <div class="form-actions">
                <button
                  @click="saveSingleDay"
                  :disabled="!isSingleDayFormValid || scheduleLoading"
                  class="btn-save"
                >
                  <svg v-if="scheduleLoading" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="loading-icon">
                    <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                  </svg>
                  <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A2,2 0 0,0 5,23H19A2,2 0 0,0 21,21V9M19,9H14V4H5V21H19V9Z"/>
                  </svg>
                  {{ scheduleLoading ? 'Saving...' : 'Update Day Schedule' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeSingleDayModal" class="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DashboardService } from '@/services/dashboard'
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

export default {
  name: 'AppointmentsView',
  components: {
    Sidebar,
    TopHeader
  },

  data () {
    return {
      loading: false,
      error: null,
      appointments: [],
      pagination: null,
      filters: {
        page: 1,
        limit: 25,
        date: '',
        status: ''
      },
      showSettingsModal: false,
      settingsLoading: false,
      calendarLinksEnabled: false,
      showWeeklyScheduleModal: false,
      scheduleLoading: false,
      weeklySchedule: [],
      showScheduleForm: false,
      scheduleForm: {
        days: [],
        start_time: '09:00',
        end_time: '17:00',
        slot_duration: 30
      },
      showSingleDayModal: false,
      singleDayForm: {
        day_of_week: null,
        day_name: '',
        start_time: '09:00',
        end_time: '17:00',
        slot_duration: 30,
        is_active: true,
        working_hours: ''
      },
      dayOptions: [
        { value: 0, label: 'Sunday' },
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' }
      ],
      paymentLoading: false,
      paymentSettings: {
        payment_required: false,
        payment_amount: 50,
        instapay_account: '',
        instapay_link: '',
        payment_configured: false
      }
    }
  },

  computed: {
    hasActiveFilters () {
      return this.filters.date || this.filters.status
    },

    calendarLinkDescription () {
      return this.calendarLinksEnabled
        ? 'Calendar links are visible to customers'
        : 'Calendar links are hidden from customers'
    },

    paymentRequiredDescription () {
      return this.paymentSettings.payment_required
        ? 'Customers must pay a down payment to book appointments'
        : 'Customers can book appointments without payment'
    },

    isPaymentFormValid () {
      if (!this.paymentSettings.payment_required) return true

      return this.paymentSettings.payment_amount > 0 &&
             this.paymentSettings.instapay_account.trim() !== '' &&
             this.paymentSettings.instapay_link.trim() !== ''
    },

    isScheduleFormValid () {
      return this.scheduleForm.days.length > 0 &&
             this.scheduleForm.start_time &&
             this.scheduleForm.end_time &&
             this.scheduleForm.slot_duration > 0
    },

    hasMixedScheduleTimes () {
      if (!this.showScheduleForm || this.weeklySchedule.length === 0) return false

      const activeSchedules = this.weeklySchedule.filter(day => day.is_active)
      if (activeSchedules.length <= 1) return false

      // Check if all active schedules have the same times and duration
      const firstSchedule = activeSchedules[0]
      return !activeSchedules.every(schedule =>
        schedule.start_time === firstSchedule.start_time &&
        schedule.end_time === firstSchedule.end_time &&
        schedule.slot_duration === firstSchedule.slot_duration
      )
    },

    isSingleDayFormValid () {
      return this.singleDayForm.start_time &&
             this.singleDayForm.end_time &&
             this.singleDayForm.slot_duration > 0
    }
  },

  mounted () {
    this.loadAppointments()
    this.loadCalendarLinkStatus()
    this.loadPaymentSettings()
    this.loadWeeklySchedule()
  },

  methods: {
    async loadAppointments () {
      try {
        this.loading = true
        this.error = null

        const result = await DashboardService.getAppointments(this.filters)

        if (result.success) {
          this.appointments = result.appointments || []
          this.pagination = result.pagination || null
        } else {
          this.error = result.message || 'Failed to load appointments'
        }
      } catch (error) {
        console.error('Error loading appointments:', error)
        this.error = 'Error loading appointments: ' + error.message
      } finally {
        this.loading = false
      }
    },

    clearFilters () {
      this.filters = {
        page: 1,
        limit: 25,
        date: '',
        status: ''
      }
      this.loadAppointments()
    },

    goToPage (page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.filters.page = page
        this.loadAppointments()
      }
    },

    getPageNumbers () {
      if (!this.pagination) return []

      const current = this.pagination.current_page
      const last = this.pagination.last_page
      const pages = []

      // Show up to 5 page numbers
      let start = Math.max(1, current - 2)
      const end = Math.min(last, start + 4)

      // Adjust start if we're near the end
      if (end - start < 4) {
        start = Math.max(1, end - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },

    formatDate (dateString) {
      if (!dateString) return '-'

      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },

    formatDateTime (dateString) {
      if (!dateString) return '-'

      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    formatPhone (phone) {
      if (!phone) return '-'

      // Format Egyptian phone numbers
      if (phone.startsWith('20')) {
        return '+' + phone.substring(0, 2) + ' ' + phone.substring(2, 4) + ' ' + phone.substring(4, 8) + ' ' + phone.substring(8)
      }

      return phone
    },

    getStatusClass (status) {
      switch (status) {
        case 'booked':
          return 'status-booked'
        case 'completed':
          return 'status-completed'
        case 'cancelled':
          return 'status-cancelled'
        default:
          return 'status-unknown'
      }
    },

    getStatusText (status) {
      switch (status) {
        case 'booked':
          return 'Booked'
        case 'completed':
          return 'Completed'
        case 'cancelled':
          return 'Cancelled'
        default:
          return status || 'Unknown'
      }
    },

    truncateFeedback (feedback) {
      if (!feedback) return ''
      return feedback.length > 50 ? feedback.substring(0, 50) + '...' : feedback
    },

    async loadCalendarLinkStatus () {
      try {
        this.settingsLoading = true
        const result = await DashboardService.getCalendarLinkStatus()

        if (result.success) {
          this.calendarLinksEnabled = result.calendar_links_enabled
        } else {
          console.error('Failed to load calendar link status:', result.message)
        }
      } catch (error) {
        console.error('Error loading calendar link status:', error)
      } finally {
        this.settingsLoading = false
      }
    },

    async toggleCalendarLinks () {
      try {
        this.settingsLoading = true
        const result = await DashboardService.toggleCalendarLinks()

        if (result.success) {
          this.calendarLinksEnabled = result.calendar_links_enabled
          console.log(result.message)
        } else {
          console.error('Failed to toggle calendar links:', result.message)
          // Revert the change if it failed
          this.calendarLinksEnabled = !this.calendarLinksEnabled
        }
      } catch (error) {
        console.error('Error toggling calendar links:', error)
        // Revert the change if it failed
        this.calendarLinksEnabled = !this.calendarLinksEnabled
      } finally {
        this.settingsLoading = false
      }
    },

    closeSettingsModal () {
      this.showSettingsModal = false
    },

    closeWeeklyScheduleModal () {
      this.showWeeklyScheduleModal = false
      this.showScheduleForm = false
      this.resetScheduleForm()
    },

    resetScheduleForm () {
      this.scheduleForm = {
        days: [],
        start_time: '09:00',
        end_time: '17:00',
        slot_duration: 30
      }
    },

    populateScheduleForm () {
      if (this.weeklySchedule.length === 0) {
        this.resetScheduleForm()
        return
      }

      // Get all active days
      const activeDays = this.weeklySchedule
        .filter(day => day.is_active)
        .map(day => day.day_of_week)

      // Try to find common time settings
      const activeSchedules = this.weeklySchedule.filter(day => day.is_active)

      if (activeSchedules.length > 0) {
        // Use the most common start_time, end_time, and slot_duration
        const startTimes = activeSchedules.map(day => day.start_time)
        const endTimes = activeSchedules.map(day => day.end_time)
        const slotDurations = activeSchedules.map(day => day.slot_duration)

        // Find the most frequent values or use the first one
        const mostCommonStartTime = this.getMostFrequent(startTimes) || '09:00'
        const mostCommonEndTime = this.getMostFrequent(endTimes) || '17:00'
        const mostCommonSlotDuration = this.getMostFrequent(slotDurations) || 30

        this.scheduleForm = {
          days: activeDays,
          start_time: this.convertTo24Hour(mostCommonStartTime),
          end_time: this.convertTo24Hour(mostCommonEndTime),
          slot_duration: mostCommonSlotDuration
        }
      } else {
        this.resetScheduleForm()
      }
    },

    getMostFrequent (arr) {
      if (arr.length === 0) return null

      const frequency = {}
      let maxCount = 0
      let mostFrequent = arr[0]

      arr.forEach(item => {
        frequency[item] = (frequency[item] || 0) + 1
        if (frequency[item] > maxCount) {
          maxCount = frequency[item]
          mostFrequent = item
        }
      })

      return mostFrequent
    },

    convertTo24Hour (time12h) {
      if (!time12h || (time12h.includes(':') && (!time12h.includes('AM') && !time12h.includes('PM')))) {
        return time12h // Already in 24-hour format or invalid
      }

      const [time, modifier] = time12h.split(' ')
      let [hours, minutes] = time.split(':')

      if (hours === '12') {
        hours = '00'
      }

      if (modifier === 'PM') {
        hours = parseInt(hours, 10) + 12
      }

      return `${hours.toString().padStart(2, '0')}:${minutes}`
    },

    createNewSchedule () {
      this.resetScheduleForm()
      this.showScheduleForm = true
    },

    editSchedule () {
      this.populateScheduleForm()
      this.showScheduleForm = true
    },

    // Single Day Operations
    editSingleDay (day) {
      this.singleDayForm = {
        day_of_week: day.day_of_week,
        day_name: day.day_name,
        start_time: this.convertTo24Hour(day.start_time),
        end_time: this.convertTo24Hour(day.end_time),
        slot_duration: day.slot_duration,
        is_active: day.is_active,
        working_hours: day.working_hours
      }
      this.showSingleDayModal = true
    },

    async disableSingleDay (day) {
      if (!confirm(`Are you sure you want to close ${day.day_name}?`)) return

      try {
        this.scheduleLoading = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch(`https://api.quali-d.com/appointments/schedule/${day.day_of_week}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const result = await response.json()

        if (result.success) {
          await this.loadWeeklySchedule()
          console.log(`${day.day_name} has been closed`)
        } else {
          console.error('Failed to disable day:', result.message)
        }
      } catch (error) {
        console.error('Error disabling day:', error)
      } finally {
        this.scheduleLoading = false
      }
    },

    async enableSingleDay (day) {
      try {
        this.scheduleLoading = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch(`https://api.quali-d.com/appointments/schedule/${day.day_of_week}/enable`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const result = await response.json()

        if (result.success) {
          await this.loadWeeklySchedule()
          console.log(`${day.day_name} has been enabled`)
        } else {
          console.error('Failed to enable day:', result.message)
        }
      } catch (error) {
        console.error('Error enabling day:', error)
      } finally {
        this.scheduleLoading = false
      }
    },

    closeSingleDayModal () {
      this.showSingleDayModal = false
      this.singleDayForm = {
        day_of_week: null,
        day_name: '',
        start_time: '09:00',
        end_time: '17:00',
        slot_duration: 30,
        is_active: true,
        working_hours: ''
      }
    },

    async saveSingleDay () {
      try {
        this.scheduleLoading = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch(`https://api.quali-d.com/appointments/schedule/${this.singleDayForm.day_of_week}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            start_time: this.singleDayForm.start_time,
            end_time: this.singleDayForm.end_time,
            slot_duration: this.singleDayForm.slot_duration
          })
        })

        const result = await response.json()

        if (result.success) {
          await this.loadWeeklySchedule()
          this.closeSingleDayModal()
          console.log(`${this.singleDayForm.day_name} schedule updated successfully`)
        } else {
          console.error('Failed to update day schedule:', result.message)
        }
      } catch (error) {
        console.error('Error updating day schedule:', error)
      } finally {
        this.scheduleLoading = false
      }
    },

    async loadWeeklySchedule () {
      try {
        this.scheduleLoading = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/appointments/schedule', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const result = await response.json()

        if (result.success) {
          this.weeklySchedule = result.schedules || []
        } else {
          console.error('Failed to load weekly schedule:', result.message)
          this.weeklySchedule = []
        }
      } catch (error) {
        console.error('Error loading weekly schedule:', error)
        this.weeklySchedule = []
      } finally {
        this.scheduleLoading = false
      }
    },

    async saveSchedule () {
      try {
        this.scheduleLoading = true
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/appointments/bulk-schedule', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            days: this.scheduleForm.days,
            start_time: this.scheduleForm.start_time,
            end_time: this.scheduleForm.end_time,
            slot_duration: this.scheduleForm.slot_duration
          })
        })

        const result = await response.json()

        if (result.success) {
          await this.loadWeeklySchedule()
          this.showScheduleForm = false
          console.log('Schedule saved successfully')
        } else {
          console.error('Failed to save schedule:', result.message)
        }
      } catch (error) {
        console.error('Error saving schedule:', error)
      } finally {
        this.scheduleLoading = false
      }
    },

    async loadPaymentSettings () {
      try {
        this.paymentLoading = true
        const result = await DashboardService.getPaymentSettings()

        if (result.success) {
          this.paymentSettings = {
            payment_required: result.payment_required || false,
            payment_amount: result.payment_amount || 50,
            instapay_account: result.instapay_account || '',
            instapay_link: result.instapay_link || '',
            payment_configured: result.payment_configured || false
          }
        } else {
          console.error('Failed to load payment settings:', result.message)
        }
      } catch (error) {
        console.error('Error loading payment settings:', error)
      } finally {
        this.paymentLoading = false
      }
    },

    onPaymentRequiredChange () {
      // If payment is disabled, don't need to save immediately
      if (!this.paymentSettings.payment_required) {
        this.savePaymentSettings()
      }
    },

    async savePaymentSettings () {
      try {
        this.paymentLoading = true

        const paymentData = {
          payment_required: this.paymentSettings.payment_required,
          payment_amount: this.paymentSettings.payment_amount,
          instapay_account: this.paymentSettings.instapay_account,
          instapay_link: this.paymentSettings.instapay_link
        }

        let result
        if (this.paymentSettings.payment_configured) {
          // Update existing settings
          result = await DashboardService.updatePaymentSettings(paymentData)
        } else {
          // Create new settings
          result = await DashboardService.createPaymentSettings(paymentData)
        }

        if (result.success) {
          this.paymentSettings.payment_configured = true
          console.log('Payment settings saved successfully')
        } else {
          console.error('Failed to save payment settings:', result.message)
        }
      } catch (error) {
        console.error('Error saving payment settings:', error)
      } finally {
        this.paymentLoading = false
      }
    }
  }
}
</script>

<style scoped>
.appointments-layout {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.appointments-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Filters Section */
.filters-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  margin-bottom: 24px;
}

.filters-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 16px;
  align-items: end;
}

.action-buttons {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.filter-input, .filter-select {
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: all 0.2s;
}

.filter-input:focus, .filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.btn-settings {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #6366f1;
  color: white;
  border: 1px solid #6366f1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-settings:hover {
  background: #5855eb;
  border-color: #5855eb;
}

.btn-schedule {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #059669;
  color: white;
  border: 1px solid #059669;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-schedule:hover {
  background: #047857;
  border-color: #047857;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: #6b7280;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: #dc2626;
  text-align: center;
}

.error-icon {
  color: #dc2626;
}

.error-state h3 {
  color: #dc2626;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.error-state p {
  margin: 0;
  color: #6b7280;
}

/* Table */
.table-container {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.appointments-table {
  width: 100%;
  border-collapse: collapse;
}

.appointments-table th {
  background: #f8fafc;
  padding: 16px 20px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.appointments-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  color: #1f2937;
  vertical-align: top;
}

.appointment-row:hover {
  background: #f8fafc;
}

/* Customer Info */
.customer-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.customer-name {
  font-weight: 600;
  color: #1f2937;
}

/* Phone Link */
.phone-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.phone-link:hover {
  text-decoration: underline;
}

/* Date and Time */
.appointment-date {
  font-weight: 500;
  color: #1f2937;
}

.time-slot {
  color: #6b7280;
  font-size: 13px;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.created-date {
  color: #6b7280;
  font-size: 13px;
}

/* Status Badges */
.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-booked {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-completed {
  background: #dcfce7;
  color: #166534;
}

.status-cancelled {
  background: #fee2e2;
  color: #dc2626;
}

.status-unknown {
  background: #f3f4f6;
  color: #6b7280;
}

/* Feedback */
.feedback-cell {
  max-width: 200px;
}

.rating {
  margin-bottom: 4px;
  font-size: 14px;
}

.feedback-text {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
  cursor: help;
}

.no-feedback {
  color: #9ca3af;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 32px;
  text-align: center;
  color: #6b7280;
}

.empty-icon {
  color: #d1d5db;
}

.empty-state h3 {
  color: #374151;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.pagination-info {
  font-size: 14px;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.page-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

/* Mobile Responsive */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0 !important;
  }
}

@media (max-width: 768px) {
  .appointments-layout {
    background: #f8fafc;
    overflow-x: hidden;
  }

  .main-content {
    margin-left: 0 !important;
    width: 100%;
    max-width: 100vw;
    min-height: 100vh;
    overflow-x: hidden;
  }

  .page-content {
    padding: 12px;
    padding-top: 76px;
    width: 100%;
    box-sizing: border-box;
  }

  .appointments-container {
    max-width: 100%;
    width: 100%;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .filters-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-info {
    order: -1;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .page-content {
    padding: 8px;
    padding-top: 76px;
  }

  .page-header h1 {
    font-size: 20px;
  }

  .appointments-table th,
  .appointments-table td {
    padding: 12px 8px;
    font-size: 13px;
  }

  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }
}

/* Settings Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-icon {
  color: #6366f1;
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.modal-footer {
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Settings Section */
.settings-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.setting-info {
  flex: 1;
}

.setting-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.setting-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.setting-control {
  flex-shrink: 0;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #d1d5db;
  transition: all 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  transition: all 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

input:checked + .toggle-slider {
  background: #10b981;
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Payment Settings */
.payment-settings-section {
  margin-top: 32px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.payment-required-toggle {
  margin-bottom: 16px;
}

.payment-form {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  background: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

.input-with-currency {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-currency .form-input {
  padding-right: 50px;
}

.currency-label {
  position: absolute;
  right: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
}

.form-actions {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #10b981;
  color: white;
  border: 1px solid #10b981;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  background: #059669;
  border-color: #059669;
}

.btn-save:disabled {
  background: #d1d5db;
  border-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

.loading-icon {
  animation: spin 1s linear infinite;
}

/* Mobile Modal */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 20px;
  }

  .setting-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .setting-control {
    align-self: flex-end;
  }
}

/* Weekly Schedule Modal */
.weekly-schedule-modal {
  max-width: 700px;
}

.schedule-icon {
  color: #059669;
}

.schedule-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* No Schedule State */
.no-schedule-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  text-align: center;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
}

.no-schedule-icon {
  color: #d1d5db;
}

.no-schedule-state h3 {
  color: #374151;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.no-schedule-state p {
  color: #6b7280;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
}

/* Existing Schedule */
.existing-schedule {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.schedule-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.schedule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.schedule-day {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
}

.schedule-day.inactive {
  background: #f9fafb;
  opacity: 0.7;
}

.schedule-day:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.day-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.status-indicator {
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
}

.status-indicator.active {
  background: #dcfce7;
  color: #166534;
}

.status-indicator.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.day-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.working-hours {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

.slot-info {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

/* Day Actions */
.day-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  justify-content: center;
}

.day-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0;
}

.day-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.day-btn-edit {
  background: #dbeafe;
  color: #1d4ed8;
}

.day-btn-edit:hover:not(:disabled) {
  background: #bfdbfe;
  color: #1e40af;
}

.day-btn-disable {
  background: #fee2e2;
  color: #dc2626;
}

.day-btn-disable:hover:not(:disabled) {
  background: #fecaca;
  color: #b91c1c;
}

.day-btn-enable {
  background: #dcfce7;
  color: #166534;
}

.day-btn-enable:hover:not(:disabled) {
  background: #bbf7d0;
  color: #15803d;
}

/* Schedule Form */
.schedule-form {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.bulk-schedule-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.mixed-schedule-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  color: #92400e;
}

.warning-icon {
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 2px;
}

.warning-content p {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}

.warning-content p:first-child {
  font-weight: 600;
  margin-bottom: 4px;
}

.days-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 8px;
}

.day-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.day-checkbox:hover {
  border-color: #059669;
  background: #f0fdf4;
}

.day-checkbox input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.day-checkbox input[type="checkbox"]:checked + .day-label {
  color: #059669;
  font-weight: 600;
}

.day-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.time-inputs-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.time-input {
  font-family: monospace;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.form-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.btn-text {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-text:hover {
  background: #f3f4f6;
  color: #374151;
}

.form-placeholder {
  color: #6b7280;
  font-style: italic;
  margin: 0;
  padding: 32px;
  text-align: center;
  background: white;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

/* Mobile Weekly Schedule Modal */
@media (max-width: 768px) {
  .weekly-schedule-modal {
    width: 95%;
    max-width: 95%;
  }

  .schedule-grid {
    grid-template-columns: 1fr;
  }

  .schedule-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .days-selector {
    grid-template-columns: 1fr 1fr;
  }

  .time-inputs-row {
    grid-template-columns: 1fr;
  }
}

/* Single Day Modal */
.single-day-modal {
  max-width: 600px;
}

.single-day-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.current-schedule-info {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.current-schedule-info h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.current-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-item span {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.status-active {
  color: #059669;
}

.status-inactive {
  color: #dc2626;
}

.single-day-edit-form {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.single-day-edit-form h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

/* Mobile Single Day Modal */
@media (max-width: 768px) {
  .single-day-modal {
    width: 95%;
    max-width: 95%;
  }

  .current-info-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
